import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, DollarSign, AlertCircle, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TaxResources = () => {
  const resources = [
    {
      icon: Calendar,
      title: '2026 Tax Filing Deadlines (2025 Tax Year)',
      items: [
        { label: 'Personal Tax Filing Deadline', date: 'April 30, 2026' },
        { label: 'Self-Employed Filing Deadline', date: 'June 15, 2026' },
        { label: 'Payment Deadline (Self-Employed)', date: 'April 30, 2026' },
        { label: 'RRSP Contribution Deadline (2025)', date: 'March 2, 2026' }
      ]
    },
    {
      icon: DollarSign,
      title: '2025 Tax Brackets (Federal)',
      items: [
        { label: 'Up to $55,867', rate: '15%' },
        { label: '$55,867 to $111,733', rate: '20.5%' },
        { label: '$111,733 to $173,205', rate: '26%' },
        { label: '$173,205 to $246,752', rate: '29%' },
        { label: 'Over $246,752', rate: '33%' }
      ]
    }
  ];

  const links = [
    { name: 'Canada Revenue Agency (CRA)', url: 'https://www.canada.ca/en/revenue-agency.html' },
    { name: 'My CRA Account (Sign In)', url: 'https://www.canada.ca/en/revenue-agency/services/e-services/cra-login-services.html' },
    { name: 'Tax-Free Savings Account (TFSA)', url: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/tax-free-savings-account.html' },
    { name: 'Canada Child Benefit (CCB)', url: 'https://www.canada.ca/en/revenue-agency/services/child-family-benefits/canada-child-benefit-overview.html' },
    { name: 'RRSP Contribution & Deduction Limits', url: 'https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/rrsps-related-plans.html' },
    { name: 'GST/HST Credit', url: 'https://www.canada.ca/en/revenue-agency/services/child-family-benefits/goods-services-tax-harmonized-sales-tax-gst-hst-credit.html' },
    { name: 'Get a Tax Refund / NETFILE', url: 'https://www.canada.ca/en/revenue-agency/services/e-services/digital-services-individuals/netfile-overview.html' },
    { name: 'Benefits & Credits Calculator', url: 'https://www.canada.ca/en/revenue-agency/services/child-family-benefits/child-family-benefits-calculator.html' },
    { name: 'Business Number (BN) Registration', url: 'https://www.canada.ca/en/services/taxes/business-number.html' },
    { name: 'GST/HST for Businesses', url: 'https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/gst-hst-businesses.html' },
    { name: 'Payroll Deductions & Remittances', url: 'https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/payroll.html' },
    { name: 'Service Canada', url: 'https://www.canada.ca/en/employment-social-development/corporate/portfolio/service-canada.html' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-16 bg-gradient-to-br from-[#E0F2FE] via-white to-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            <h1 className="text-5xl md:text-6xl font-bold text-[#0F172A] mb-6">
              Tax Resources & Guides
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Stay informed with the latest tax information, deadlines, and helpful guides
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {resources.map((resource, index) => {
              const Icon = resource.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-[#E0F2FE] rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#005782]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0F172A]">{resource.title}</h3>
                  </div>
                  <div className="space-y-3">
                    {resource.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                        <span className="text-slate-700 font-medium">{item.label}</span>
                        <span className="text-[#005782] font-semibold">{item.date || item.rate}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-8">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-2">Important Note</h3>
                <p className="text-slate-700">
                  This information is for general guidance only. Tax laws and regulations change frequently. For personalized advice specific to your situation, please contact our tax professionals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-8 text-center">
            Helpful Government Resources
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {links.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors">
                <span className="text-slate-700 font-medium">{link.name}</span>
                <ExternalLink className="w-5 h-5 text-[#005782]" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TaxResources;